.panel {
  display: grid;
  gap: var(--space-5);
}

/*
 * The rate card. It carries the price the way the auction panel's masthead carries the
 * countdown — one elevated surface stating what the day costs — but deliberately without a
 * clock or a rank mark. There is no deadline to race and no position to win here, and
 * borrowing either would be the design implying otherwise.
 */
.terms {
  display: grid;
  gap: var(--space-3);
}

.price {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-2);
}

/*
 * The one figure on this panel. Canon § 10 fixes it at $1, so it is a rate rather than a
 * value that ticks — but it stays in the figure face because it is money, and money is set
 * the same way on every Dealbid surface.
 */
.amount {
  font-family: var(--font-figure);
  font-size: var(--text-2xl);
  font-weight: 600;
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.per {
  color: var(--text-muted);
  font-size: var(--text-sm);
}

.what,
.expiry {
  color: var(--text-muted);
  max-inline-size: var(--measure);
}

/*
 * Canon § 12's sponsored disclosure, in the same treatment the auction panel gives it:
 * rule-marked board furniture that is always present, never a warning that appeared because
 * something is wrong.
 */
.disclosure {
  padding-block-start: var(--space-3);
  border-block-start: 1px solid var(--border);
  color: var(--text-muted);
  font-size: var(--text-sm);
  max-inline-size: var(--measure);
}

/* What the founder already holds for the day, before anything is offered for sale. */
.held {
  display: grid;
  gap: var(--space-3);
  /* Not read as prose, so it clears its own marker: `base.css` zeroes list margin and
   * padding for every repo but deliberately leaves `list-style` alone (`APP-018`).
   * `role="list"` on the element keeps the semantics Safari drops with the marker. */
  list-style: none;
}

.heldItem {
  display: grid;
  gap: var(--space-1);
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--border);
  border-inline-start: 3px solid var(--positive);
  border-radius: var(--radius-md);
  background: var(--surface);
}

.heldTitle {
  font-weight: 600;
}

.heldNote {
  color: var(--text-muted);
  font-size: var(--text-sm);
}

.deals {
  display: grid;
  gap: var(--space-4);
  /* Not read as prose, so it clears its own marker: `base.css` zeroes list margin and
   * padding for every repo but deliberately leaves `list-style` alone (`APP-018`).
   * `role="list"` on the element keeps the semantics Safari drops with the marker. */
  list-style: none;
}

.deal {
  display: grid;
  gap: var(--space-3);
}

/*
 * Mobile-first: the name and the control stack full width, comfortably tappable. From 30rem
 * the button sits at the end of the row beside the product name.
 */
.head {
  display: grid;
  gap: var(--space-3);
}

@media (min-width: 30rem) {
  .head {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
  }
}

.product {
  font-size: var(--text-lg);
  min-inline-size: 0;
}

.buy {
  justify-self: start;
}

@media (min-width: 30rem) {
  .buy {
    justify-self: end;
  }
}

.offer {
  color: var(--text-muted);
  font-size: var(--text-sm);
  max-inline-size: var(--measure);
}

/*
 * Why a deal cannot be listed. Neutral-edged rather than brass or green: a deal that is
 * already bidding or already listed is in a perfectly good state, and colouring this as a
 * warning would make holding a Top 10 bid look like a problem.
 */
.reason {
  padding-inline-start: var(--space-3);
  border-inline-start: 3px solid var(--border-strong);
  color: var(--text-muted);
  font-size: var(--text-sm);
  max-inline-size: var(--measure);
}

.bought {
  display: grid;
  gap: var(--space-2);
  padding: var(--space-4);
  border: 1px solid var(--border);
  border-inline-start: 3px solid var(--positive);
  border-radius: var(--radius-md);
  background: var(--surface);
  font-size: var(--text-sm);
  color: var(--text-muted);
  max-inline-size: var(--measure);
}

.boughtTitle {
  color: var(--text);
  font-weight: 600;
  font-size: var(--text-base);
}

.boughtCredit {
  padding-block-start: var(--space-2);
  border-block-start: 1px solid var(--border);
}

.failure {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-4);
  border: 1px solid var(--border);
  border-inline-start: 3px solid var(--danger);
  border-radius: var(--radius-md);
  background: var(--surface);
  color: var(--danger);
  font-size: var(--text-sm);
  max-inline-size: var(--measure);
}

/* The buyable rows waiting on the deals read, while the rest of the panel is already up. */
.pending {
  color: var(--text-muted);
  font-size: var(--text-sm);
}
